// src/components/etapaproductiva/RegistrarEProductivaMasivo.jsx

import axios from "axios";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "EtapaProductiva";

const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
  };
};

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const obtenerObjetoStorage = () => {
  const claves = ["permisos", "usuario", "user", "usuarios"];

  for (const clave of claves) {
    try {
      const valor = localStorage.getItem(clave);
      if (valor) return JSON.parse(valor);
    } catch (error) {
      console.error(`Error leyendo ${clave}:`, error);
    }
  }

  return {};
};

const obtenerModulosStorage = () => {
  const permisosData = obtenerObjetoStorage();

  const modulos = Array.isArray(permisosData)
    ? permisosData
    : permisosData?.modulos ||
      permisosData?.permisos ||
      permisosData?.usuario?.modulos ||
      permisosData?.usuario?.permisos ||
      permisosData?.user?.modulos ||
      permisosData?.user?.permisos ||
      [];

  return Array.isArray(modulos) ? modulos : [];
};

const obtenerModuloPorNombre = (nombreModulo) => {
  const modulos = obtenerModulosStorage();
  const moduloBuscado = normalizarTexto(nombreModulo);

  return (
    modulos.find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.nombre ||
        modulo?.name ||
        "";

      return normalizarTexto(nombre) === moduloBuscado;
    }) || {}
  );
};

const ACCIONES_PERMISOS = {
  crear: {
    campo: "puedeCrear",
    alias: ["crear", "create", "add", "registrar"],
  },
};

const tienePermisoAccion = (modulo = {}, accion = "") => {
  const accionLower = normalizarTexto(accion);
  const config = ACCIONES_PERMISOS[accionLower];

  if (!config) return false;

  const { campo, alias } = config;

  return (
    modulo?.[campo] === true ||
    modulo?.[accionLower] === true ||
    modulo?.acciones?.[accionLower] === true ||
    alias.some(
      (nombreAccion) =>
        modulo?.[nombreAccion] === true ||
        modulo?.acciones?.[nombreAccion] === true
    )
  );
};

const parseISO = (fecha = "") => {
  if (!fecha) return null;
  const [y, m, d] = fecha.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
};

const fmtISO = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const addMonthsClamped = (isoDate, months) => {
  const p = parseISO(isoDate);
  if (!p) return "";

  const base = new Date(p.y, p.m - 1 + months, 1);
  const lastDay = new Date(
    base.getFullYear(),
    base.getMonth() + 1,
    0
  ).getDate();

  const day = Math.min(p.d, lastDay);

  return fmtISO(base.getFullYear(), base.getMonth() + 1, day);
};

const toISODateUTC = (valor) =>
  valor ? new Date(`${valor}T00:00:00.000Z`).toISOString() : undefined;

const toNumberOrUndef = (valor) =>
  valor !== "" && valor !== null && valor !== undefined && !Number.isNaN(Number(valor))
    ? Number(valor)
    : undefined;

const RegistrarEProductivaMasivo = () => {
  const navigate = useNavigate();
  const { matriculaId } = useParams();

  const moduloEtapaProductiva = useMemo(
    () => obtenerModuloPorNombre(MODULO),
    []
  );

  const puedeCrear = useMemo(
    () => tienePermisoAccion(moduloEtapaProductiva, "crear"),
    [moduloEtapaProductiva]
  );

  const [guardando, setGuardando] = useState(false);

  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [documentoInstructor, setDocumentoInstructor] = useState("");
  const [instructorNombre, setInstructorNombre] = useState("");
  const [instructorEmail, setInstructorEmail] = useState("");
  const [instructorId, setInstructorId] = useState("");

  const [enteObjectId, setEnteObjectId] = useState("");
  const [nitEnteconformador, setNitEnteconformador] = useState("");
  const [nombreEnte, setNombreEnte] = useState("");
  const [enteBloqueadoPorAsociacion, setEnteBloqueadoPorAsociacion] =
    useState(false);

  const [jefeObjectId, setJefeObjectId] = useState("");
  const [documentoJefe, setDocumentoJefe] = useState("");
  const [nombreJefe, setNombreJefe] = useState("");
  const [estadoAsignacion, setEstadoAsignacion] = useState("");
  const [mensajeAsociacion, setMensajeAsociacion] = useState("");
  const [telefono1Jefe, setTelefono1Jefe] = useState("");
  const [correoJefe, setCorreoJefe] = useState("");

  const [estadoEtapaPractica] = useState("");
  const [fechaInicioEtapaPractica, setFechaInicioEtapaPractica] = useState("");
  const [fechaFinEtapaPractica, setFechaFinEtapaPractica] = useState("");
  const [clasificacionEtapaPractica, setClasificacionEtapaPractica] =
    useState("publica");

  const [alternativaEtapaPractica, setAlternativaEtapaPractica] = useState("");
  const [subtipoAlternativaEtapaPractica, setSubtipoAlternativaEtapaPractica] =
    useState("");
  const [alternativa, setAlternativa] = useState("");
  const [subtipo, setSubtipo] = useState("");

  const [afiliadoARL, setAfiliadoARL] = useState("");
  const [nivelRiesgoARL, setNivelRiesgoARL] = useState("");
  const [riesgoCorrespondeARL, setRiesgoCorrespondeARL] = useState("");
  const [cuentaEPP, setCuentaEPP] = useState("");

  const alternativas = useMemo(
    () => ({
      "Proyecto Productivo": [
        "Sena - Empresa",
        "Sena Proveedor Sena",
        "Producción de Centros",
        "Proyecto productivo I+D+i",
        "Proyectos Ruta Emprendedora SENA",
        "Prácticas en economía popular/campesina",
        "Convocatorias SENNOVA",
      ],
      Monitoría: [
        "Monitoría regular",
        "Monitoría convocatorias SENNOVA",
        "Otra",
      ],
      "Contrato Vínculo Formativo": [
        "Apoyo a una unidad productiva familiar",
        "De apoyo a institución estatal/ONG/sin ánimo de lucro",
        "Asesoría a pymes",
        "Prácticas en economía popular/campesina",
      ],
      "Contrato de Aprendizaje": [
        "Contrato de aprendizaje (regular)",
        "Convocatorias SENNOVA",
        "Prácticas en economía popular/campesina",
      ],
      "Vínculo Laboral": [
        "Vínculo laboral (regular)",
        "Prácticas en economía popular/campesina",
      ],
    }),
    []
  );

  const handleAlternativaChange = (e) => {
    const valor = e.target.value;

    setAlternativa(valor);
    setSubtipo("");
    setAlternativaEtapaPractica(valor);
    setSubtipoAlternativaEtapaPractica("");
  };

  const handleInicioChange = (e) => {
    const valor = e.target.value;

    setFechaInicioEtapaPractica(valor);
    setFechaFinEtapaPractica(valor ? addMonthsClamped(valor, 6) : "");
  };

  const cleanData = useCallback(() => {
    setClasificacionEtapaPractica("publica");

    setAlternativa("");
    setSubtipo("");
    setAlternativaEtapaPractica("");
    setSubtipoAlternativaEtapaPractica("");

    setNitEnteconformador("");
    setNombreEnte("");
    setEnteObjectId("");
    setEnteBloqueadoPorAsociacion(false);

    setDocumentoJefe("");
    setNombreJefe("");
    setEstadoAsignacion("");
    setMensajeAsociacion("");
    setTelefono1Jefe("");
    setCorreoJefe("");
    setJefeObjectId("");

    setDocumentoInstructor("");
    setInstructorNombre("");
    setInstructorEmail("");
    setInstructorId("");
    setInstructorObjectId("");

    setAfiliadoARL("");
    setNivelRiesgoARL("");
    setRiesgoCorrespondeARL("");
    setCuentaEPP("");

    setFechaInicioEtapaPractica("");
    setFechaFinEtapaPractica("");
  }, []);

  const validateMasivo = useCallback(() => {
    const missing = [];

    if (!matriculaId) missing.push("Matrícula desde la ruta");
    if (!clasificacionEtapaPractica) missing.push("Clasificación de la etapa");
    if (!alternativaEtapaPractica) missing.push("Alternativa de etapa productiva");
    if (!afiliadoARL) missing.push("Afiliación ARL");
    if (!riesgoCorrespondeARL) missing.push("¿Riesgo corresponde?");
    if (!cuentaEPP) missing.push("¿Cuenta con EPP?");

    if (!documentoInstructor || Number.isNaN(Number(documentoInstructor))) {
      missing.push("Documento del instructor válido");
    }

    if (fechaInicioEtapaPractica && fechaFinEtapaPractica) {
      const inicio = new Date(`${fechaInicioEtapaPractica}T00:00:00`);
      const fin = new Date(`${fechaFinEtapaPractica}T00:00:00`);

      if (fin < inicio) {
        Swal.fire({
          icon: "warning",
          title: "Rango de fechas inválido",
          text: "La fecha de fin no puede ser anterior a la fecha de inicio.",
        });

        return false;
      }
    }

    if (missing.length) {
      Swal.fire({
        icon: "warning",
        title: "Faltan datos obligatorios",
        html: `<ul class="text-start mb-0">${missing
          .map((item) => `<li>${item}</li>`)
          .join("")}</ul>`,
      });

      return false;
    }

    if (!Object.keys(alternativas).includes(alternativaEtapaPractica)) {
      Swal.fire({
        icon: "warning",
        title: "Alternativa inválida",
      });

      return false;
    }

    return true;
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    alternativas,
    clasificacionEtapaPractica,
    cuentaEPP,
    documentoInstructor,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    matriculaId,
    riesgoCorrespondeARL,
  ]);

  const buildPayloadMasivo = useCallback(() => {
    const raw = {
      soloActivos: false,

      enteObjectId,
      nitEnteconformador: toNumberOrUndef(nitEnteconformador),

      instructorObjectId,
      documentoInstructor: toNumberOrUndef(documentoInstructor),

      jefeObjectId,
      documentoJefe: toNumberOrUndef(documentoJefe),

      estadoEtapaPractica,
      fechaInicioEtapaPractica: toISODateUTC(fechaInicioEtapaPractica),
      fechaFinEtapaPractica: toISODateUTC(fechaFinEtapaPractica),

      clasificacionEtapaPractica,
      alternativaEtapaPractica,
      subtipoAlternativaEtapaPractica,

      afiliadoARL,
      nivelRiesgoARL: toNumberOrUndef(nivelRiesgoARL),
      riesgoCorrespondeARL,
      cuentaEPP,
    };

    return Object.fromEntries(
      Object.entries(raw).filter(
        ([, valor]) => !(typeof valor === "string" && valor.trim() === "")
      )
    );
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    clasificacionEtapaPractica,
    cuentaEPP,
    documentoInstructor,
    documentoJefe,
    enteObjectId,
    estadoEtapaPractica,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    instructorObjectId,
    jefeObjectId,
    nitEnteconformador,
    nivelRiesgoARL,
    riesgoCorrespondeARL,
    subtipoAlternativaEtapaPractica,
  ]);

  const saveMasivoEPractica = useCallback(async () => {
    if (!puedeCrear) {
      Swal.fire({
        icon: "warning",
        title: "Sin permisos",
        text: "No tienes permisos para registrar etapas prácticas masivas.",
      });
      return;
    }

    try {
      if (!validateMasivo()) return;

      const confirmacion = await Swal.fire({
        icon: "question",
        title: "Confirmar registro masivo",
        html: "Se registrará la etapa productiva para todos los aprendices asociados a esta matrícula.",
        showCancelButton: true,
        confirmButtonText: "Sí, registrar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmacion.isConfirmed) return;

      setGuardando(true);

      const { data } = await axios.post(
        `/api/etapapractica/addManyByMatricula/${matriculaId}`,
        buildPayloadMasivo(),
        authConfig()
      );

      if (data?.ok === false) {
        Swal.fire({
          icon: "warning",
          title: "No se pudo registrar",
          text: data?.message || "Ocurrió un problema al guardar masivamente.",
        });
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Registro masivo exitoso",
        text:
          data?.message ||
          "Las etapas prácticas fueron registradas correctamente.",
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });

      navigate("/etapaproductiva/listareproductiva");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al guardar masivamente",
        text:
          error?.response?.data?.message ||
          "No se pudo realizar el registro masivo.",
      });
    } finally {
      setGuardando(false);
    }
  }, [
    buildPayloadMasivo,
    matriculaId,
    navigate,
    puedeCrear,
    validateMasivo,
  ]);

  const actions = useCallback(
    (e) => {
      e.preventDefault();
      saveMasivoEPractica();
    },
    [saveMasivoEPractica]
  );

  const verificarNit = useCallback(async (nit) => {
    if (!nit) return;

    try {
      const { data } = await axios.get(
        `/api/ente/verificar/${encodeURIComponent(nit)}`,
        authConfig()
      );

      if (data?.exists) {
        setNombreEnte(data.nombreEnteconformador || "");
        setEnteObjectId(data.enteId || data._id || data.enteObjectId || "");
        setEnteBloqueadoPorAsociacion(false);
      } else {
        setNombreEnte("");
        setEnteObjectId("");
        setEnteBloqueadoPorAsociacion(false);
      }
    } catch (error) {
      console.error("Error al verificar el NIT:", error);

      setNombreEnte("");
      setEnteObjectId("");
      setEnteBloqueadoPorAsociacion(false);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "No se pudo verificar el NIT.",
      });
    }
  }, []);

  const verificarJefeActivo = useCallback(async (doc) => {
    if (!doc) return;

    try {
      const { data } = await axios.get(
        `/api/asociarjefes/verificar/${encodeURIComponent(doc)}/Activo`,
        authConfig()
      );

      if (data?.ok && data.exists) {
        setDocumentoJefe(String(doc));
        setNombreJefe(data.nombreJefe || "");
        setEstadoAsignacion(data.estadoAsignacion || "Activo");
        setMensajeAsociacion("Asociación activa encontrada.");

        const jefeId =
          data.jefeObjectId?._id ||
          data.jefeObjectId ||
          data.asociacion?.jefeObjectId?._id ||
          data.asociacion?.jefeObjectId ||
          data.jefeId ||
          "";

        setJefeObjectId(jefeId || "");

        setTelefono1Jefe(
          data.telefono1Jefe ||
            data.asociacion?.jefeObjectId?.telefono1Jefe ||
            ""
        );

        setCorreoJefe(
          data.correoJefe ||
            data.asociacion?.jefeObjectId?.correoJefe ||
            data.asociacion?.jefeObjectId?.emailJefe ||
            ""
        );

        const enteNombre =
          data.enteConformador ||
          data.asociacion?.enteObjectId?.nombreEnteconformador ||
          "";

        const enteNit =
          data.asociacion?.enteObjectId?.nitEnteconformador || "";

        const enteId =
          data.asociacion?.enteObjectId?._id ||
          data.asociacion?.enteObjectId ||
          "";

        if (enteNombre) setNombreEnte(enteNombre);
        if (enteNit) setNitEnteconformador(String(enteNit));
        if (enteId) setEnteObjectId(String(enteId));

        if (enteNombre || enteNit) {
          setEnteBloqueadoPorAsociacion(true);
        }
      } else {
        setNombreJefe("");
        setEstadoAsignacion("");
        setMensajeAsociacion(
          data?.message || "El jefe no tiene asociación activa."
        );
        setTelefono1Jefe("");
        setCorreoJefe("");
        setJefeObjectId("");
        setEnteBloqueadoPorAsociacion(false);
      }
    } catch (error) {
      console.error("Error al verificar la asociación activa:", error);

      setNombreJefe("");
      setEstadoAsignacion("");
      setMensajeAsociacion("Error al verificar la asociación activa.");
      setTelefono1Jefe("");
      setCorreoJefe("");
      setJefeObjectId("");
      setEnteBloqueadoPorAsociacion(false);
    }
  }, []);

  const verificarInstructor = useCallback(async (doc) => {
    if (!doc) return;

    try {
      const { data } = await axios.get(
        `/api/instructores/verificar/${encodeURIComponent(doc)}`,
        authConfig()
      );

      if (data?.exists) {
        setDocumentoInstructor(String(doc));
        setInstructorNombre(data.nombreCompleto || "");
        setInstructorEmail(data.emailInstructor || "");

        const instId =
          data.instructorObjectId?._id ||
          data.instructorObjectId ||
          data.instructor?._id ||
          data.instructorId ||
          "";

        setInstructorId(instId || "");
        setInstructorObjectId(instId || "");
      } else {
        setInstructorNombre("");
        setInstructorEmail("");
        setInstructorId("");
        setInstructorObjectId("");
      }
    } catch (error) {
      console.error("Error al verificar el documento del instructor:", error);

      setInstructorNombre("");
      setInstructorEmail("");
      setInstructorId("");
      setInstructorObjectId("");
    }
  }, []);

  const handleDocInstructorChange = (e) => {
    setDocumentoInstructor(e.target.value);
  };

  const titulo = "Registro Masivo de Etapa Práctica";

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h5 className="mt-2 mb-1 fw-semibold">{titulo}</h5>
                </div>

                <small className="text-secondary">
                  Este formulario registra la etapa práctica para todos los aprendices asociados a la matrícula.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                  title="Limpiar"
                  disabled={guardando}
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => navigate(-1)}
                  title="Cancelar"
                  disabled={guardando}
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={actions}>
            <div className="p-3 p-md-4">
              <div className="border rounded-4 p-3 mb-3 bg-body-tertiary">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="fw-semibold">
                    <i className="fa-solid fa-shield-halved me-2"></i>
                    Clasificación del documento
                  </div>

                  <span className="small text-secondary">
                    Selección actual: <strong>{clasificacionEtapaPractica}</strong>
                  </span>
                </div>

                <div className="btn-group w-100 mt-3" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="clasificacion"
                    id="opt-publica"
                    autoComplete="off"
                    value="publica"
                    checked={clasificacionEtapaPractica === "publica"}
                    onChange={(e) => setClasificacionEtapaPractica(e.target.value)}
                  />

                  <label className="btn btn-outline-success" htmlFor="opt-publica">
                    Pública
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="clasificacion"
                    id="opt-clasificada"
                    autoComplete="off"
                    value="clasificada"
                    checked={clasificacionEtapaPractica === "clasificada"}
                    onChange={(e) => setClasificacionEtapaPractica(e.target.value)}
                  />

                  <label className="btn btn-outline-primary" htmlFor="opt-clasificada">
                    Pública Clasificada
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="clasificacion"
                    id="opt-reservada"
                    autoComplete="off"
                    value="reservada"
                    checked={clasificacionEtapaPractica === "reservada"}
                    onChange={(e) => setClasificacionEtapaPractica(e.target.value)}
                  />

                  <label className="btn btn-outline-warning" htmlFor="opt-reservada">
                    Pública Reservada
                  </label>
                </div>
              </div>

              <div className="border rounded-4 p-3 mb-3">
                <div className="fw-semibold mb-2">
                  <i className="fa-solid fa-calendar-days me-2"></i>
                  Período de etapa práctica
                </div>

                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-3">
                    <label className="form-label small">Fecha Inicio</label>

                    <input
                      type="date"
                      className="form-control"
                      value={fechaInicioEtapaPractica}
                      onChange={handleInicioChange}
                    />
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label small">
                      Fecha Fin auto +6 meses
                    </label>

                    <input
                      type="date"
                      className="form-control"
                      value={fechaFinEtapaPractica}
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                </div>
              </div>

              <ul className="nav nav-tabs mt-2" id="myTab" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="EnteCoformador-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#EnteCoformador"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-building me-2"></i>
                    Ente / Jefe
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="InstructorAsignado-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#InstructorAsignado"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-user-tie me-2"></i>
                    Instructor
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="Alternativa-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#Alternativa"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-list-check me-2"></i>
                    Alternativa
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="RiesgosLaborales-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#RiesgosLaborales"
                    type="button"
                    role="tab"
                  >
                    <i className="fa-solid fa-helmet-safety me-2"></i>
                    Riesgos
                  </button>
                </li>
              </ul>

              <div
                className="tab-content p-3 border border-top-0 bg-white rounded-bottom-4"
                id="myTabContent"
              >
                <div
                  className="tab-pane fade show active"
                  id="EnteCoformador"
                  role="tabpanel"
                >
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label small">Documento Jefe</label>

                      <input
                        type="text"
                        className="form-control"
                        value={documentoJefe}
                        onChange={(e) => setDocumentoJefe(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            verificarJefeActivo(e.target.value);
                          }
                        }}
                        onBlur={(e) => verificarJefeActivo(e.target.value)}
                        placeholder="Ej: 12345678"
                      />
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small">Nombre del Jefe</label>

                      <input
                        type="text"
                        className="form-control"
                        value={nombreJefe}
                        onChange={(e) => setNombreJefe(e.target.value)}
                        placeholder="Se autollenará si tiene asociación activa"
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">Teléfono Jefe</label>

                      <input
                        type="text"
                        className="form-control"
                        value={telefono1Jefe}
                        onChange={(e) => setTelefono1Jefe(e.target.value)}
                        placeholder="Teléfono"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label small">Email Jefe</label>

                      <input
                        type="email"
                        className="form-control"
                        value={correoJefe}
                        onChange={(e) => setCorreoJefe(e.target.value)}
                        placeholder="correo@dominio.com"
                      />
                    </div>

                    <div className="col-12">
                      {mensajeAsociacion && (
                        <div
                          className={`alert ${
                            estadoAsignacion === "Activo"
                              ? "alert-success"
                              : "alert-warning"
                          } py-2 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2`}
                        >
                          <div>
                            <i className="fa-solid fa-circle-info me-2"></i>
                            <strong>{mensajeAsociacion}</strong>
                          </div>

                          {estadoAsignacion && (
                            <span className="badge bg-dark">
                              Estado: {estadoAsignacion}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">
                        Nit Empresa{" "}
                        {enteBloqueadoPorAsociacion && (
                          <span className="badge bg-secondary ms-1">
                            Bloqueado
                          </span>
                        )}
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        value={nitEnteconformador}
                        onChange={(e) => setNitEnteconformador(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            verificarNit(e.target.value);
                          }
                        }}
                        onBlur={(e) => verificarNit(e.target.value)}
                        placeholder="Ej: 900123456"
                        disabled={enteBloqueadoPorAsociacion}
                      />
                    </div>

                    <div className="col-md-10">
                      <label className="form-label small">
                        Nombre del Ente{" "}
                        {enteBloqueadoPorAsociacion && (
                          <span className="badge bg-secondary ms-1">
                            Bloqueado
                          </span>
                        )}
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        value={nombreEnte}
                        onChange={(e) => setNombreEnte(e.target.value)}
                        disabled={enteBloqueadoPorAsociacion}
                      />

                      <input type="hidden" value={enteObjectId} readOnly />

                      {enteBloqueadoPorAsociacion && (
                        <div className="form-text">
                          El Ente proviene de la asociación activa del jefe. Para
                          editarlo, cambia o elimina el jefe y vuelve a verificar.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="tab-pane fade"
                  id="InstructorAsignado"
                  role="tabpanel"
                >
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label small">
                        Documento Instructor
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        value={documentoInstructor}
                        onChange={handleDocInstructorChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            verificarInstructor(e.target.value);
                          }
                        }}
                        onBlur={(e) => verificarInstructor(e.target.value)}
                        placeholder="Ej: 1077866875"
                      />

                      <input type="hidden" value={instructorId} readOnly />
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small">
                        Nombre Instructor
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        value={instructorNombre}
                        onChange={(e) => setInstructorNombre(e.target.value)}
                        placeholder="Nombre del instructor"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">
                        Email Instructor
                      </label>

                      <input
                        type="email"
                        className="form-control"
                        value={instructorEmail}
                        onChange={(e) => setInstructorEmail(e.target.value)}
                        placeholder="correo@dominio.com"
                      />
                    </div>

                    <div className="col-12">
                      <div className="alert alert-light border py-2 mb-0">
                        <i className="fa-solid fa-circle-question me-2"></i>
                        Tip: escribe el documento y presiona <strong>TAB</strong> para autocompletar.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tab-pane fade" id="Alternativa" role="tabpanel">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small">
                        Alternativa de Etapa Productiva
                      </label>

                      <select
                        className="form-select"
                        value={alternativa}
                        onChange={handleAlternativaChange}
                      >
                        <option value="">-- Seleccione --</option>

                        {Object.keys(alternativas).map((alt) => (
                          <option key={alt} value={alt}>
                            {alt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small">
                        Subtipo de Alternativa
                      </label>

                      <select
                        className="form-select"
                        value={subtipo}
                        onChange={(e) => {
                          setSubtipo(e.target.value);
                          setSubtipoAlternativaEtapaPractica(e.target.value);
                        }}
                        disabled={!alternativa}
                      >
                        <option value="">-- Seleccione --</option>

                        {alternativa &&
                          alternativas[alternativa].map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                      </select>

                      {!alternativa && (
                        <div className="form-text">
                          Primero selecciona una alternativa.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="tab-pane fade"
                  id="RiesgosLaborales"
                  role="tabpanel"
                >
                  <div className="card border-0">
                    <div className="card-header bg-dark text-white text-center fw-semibold rounded-3">
                      Decreto 055 de 2015 - Riesgos Laborales
                    </div>

                    <div className="card-body px-0 pt-3">
                      <div className="alert alert-secondary text-center py-2 mb-3">
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        Este espacio debe ser siempre diligenciado.
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Afiliado a ARL?
                          </label>

                          <select
                            className="form-select"
                            value={afiliadoARL}
                            onChange={(e) => setAfiliadoARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            Nivel de riesgo
                          </label>

                          <select
                            className="form-select"
                            value={nivelRiesgoARL}
                            onChange={(e) => setNivelRiesgoARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="1">Nivel 1</option>
                            <option value="2">Nivel 2</option>
                            <option value="3">Nivel 3</option>
                            <option value="4">Nivel 4</option>
                            <option value="5">Nivel 5</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Riesgo corresponde?
                          </label>

                          <select
                            className="form-select"
                            value={riesgoCorrespondeARL}
                            onChange={(e) =>
                              setRiesgoCorrespondeARL(e.target.value)
                            }
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Cuenta con EPP?
                          </label>

                          <select
                            className="form-select"
                            value={cuentaEPP}
                            onChange={(e) => setCuentaEPP(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                            <option value="NA">NA</option>
                          </select>
                        </div>
                      </div>

                      <div className="alert alert-light border mt-3 mb-0">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        Recuerda: si el aprendiz no requiere EPP, selecciona <strong>NA</strong>.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-top bg-white px-3 py-3 d-flex justify-content-end gap-2 sticky-bottom">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                  disabled={guardando}
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => navigate(-1)}
                  disabled={guardando}
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn btn-dark"
                  disabled={guardando}
                >
                  <i
                    className={`fa-solid ${
                      guardando ? "fa-spinner fa-spin" : "fa-floppy-disk"
                    } me-2`}
                  ></i>
                  {guardando ? "Guardando..." : "Guardar masivo"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEProductivaMasivo;
